import { DatasetFreshnessStateMutator } from '@/domain/states/DatasetFreshnessState'

import { DatasetCache, DatasetFreshness } from './DatasetCache'
import { DatasetId } from './datasetPolicies'

import type { IHttpClient } from '@/infra/http/IHttpClient'
import { HTTP_TIMEOUTS } from '@/infra/http/timeouts'
import type { ILogger } from '@/infra/logger/ILogger'

export interface DatasetBytesResult {
  bytes: Uint8Array
  freshness: DatasetFreshness
  /** `true` quando os bytes vieram do cache local, sem tocar a rede. */
  fromCache: boolean
  /** Carimbo de origem da entrada usada, para o chamador decidir se precisa reprocessar. */
  sourceStamp: string | null
}

/**
 * `Last-Modified` como carimbo de origem.
 *
 * É o único header útil que dá para ler nos `.frb`/`.fgb` do CDN: ao contrário do `ETag`, ele **é**
 * CORS-safelisted. Se o valor não mudou, o conteúdo não mudou.
 */
export function readSourceStamp(headers: Record<string, unknown> | undefined): string | null {
  const value = headers?.['last-modified']

  return typeof value === 'string' ? value : null
}

/**
 * Carrega os bytes de um dataset binário (`.frb`, `.fgb`) com cache local e TTL.
 *
 * Política, em ordem:
 * 1. **cache dentro do soft TTL** → devolve sem tocar a rede (é o ganho do reload);
 * 2. **cache além do soft TTL** → tenta a rede primeiro; se a rede falhar, usa o local em silêncio
 *    (ainda está dentro do hard TTL, então não é dado vencido);
 * 3. **sem cache** → rede, grava e devolve.
 *
 * 4. **cache além do hard TTL** → tenta a rede primeiro; se a rede falhar, usa o local **e publica o
 *    estado degradado** em `DatasetFreshnessState`, que o banner global transforma em aviso visível.
 *
 * O passo 2 já devolveu o cache sem ir à rede, deixando a revalidação para quem chamava — e ninguém
 * revalidava. Na prática o TTL efetivo era o hard (24 h para espaços aéreos, 3 h para NOTAM), e
 * remoções publicadas pelo DECEA demoravam horas para sumir do app. Ir à rede aqui não pesa como o
 * primeiro download: dentro do `max-age=3600` o browser serve do próprio cache HTTP, e depois disso o
 * CDN responde 304 enquanto o `Last-Modified` não muda.
 *
 * O passo 4 é o que a política aprovada pede: nunca esconder a camada (esconder cria falso negativo —
 * "não há NOTAM" quando o correto é "não sabemos"), e nunca mostrar dado vencido em silêncio. Ele só
 * pôde ser ligado junto com o banner; antes dele existir, este ramo devolvia `null`.
 *
 * Usa o `IHttpClient` injetado, e não `fetch` nativo: a única vantagem do `fetch` seria a opção
 * `cache` (para forçar revalidação condicional), e ela não é necessária aqui — o `waypoints_full` vem
 * com `cache-control: no-cache` e os `.frb`/`.fgb` com `max-age=3600`, então o próprio browser já
 * revalida na hora certa. Em troca, o cliente injetado preserva o retry de transporte, os timeouts por
 * classe e a possibilidade de mockar em teste.
 */
export async function loadDatasetBytes(
  httpClient: IHttpClient,
  cache: DatasetCache,
  id: DatasetId,
  url: string,
  logger?: ILogger
): Promise<DatasetBytesResult | null> {
  const cached = await cache.read(id)
  const hasUsableCache = Boolean(cached && cached.kind === 'binary')

  if (cached && hasUsableCache && cached.freshness === 'fresh') {
    DatasetFreshnessStateMutator.report(id, { loadedAt: new Date(cached.meta.fetchedAt), isDegraded: false })

    return {
      bytes: new Uint8Array(cached.payload as ArrayBuffer),
      freshness: cached.freshness,
      fromCache: true,
      sourceStamp: cached.meta.sourceStamp
    }
  }

  const response = await httpClient.get<ArrayBuffer>(url, {
    timeout: HTTP_TIMEOUTS.dataset,
    responseType: 'arraybuffer'
  })

  if (response.success === false || response.status < 200 || response.status >= 300) {
    logger?.warn('DatasetCache', `${id}: falha ao baixar`)

    // A rede falhou e existe cópia local: usar é melhor que não ter dado nenhum. Se ela já passou do
    // hard TTL, o usuário precisa SABER — o estado degradado é o que o banner global lê para avisar.
    if (cached && hasUsableCache) {
      const isDegraded = cached.freshness === 'expired'

      logger?.warn('DatasetCache', `${id}: usando cópia ${isDegraded ? 'vencida' : 'local'} (rede indisponível)`)
      DatasetFreshnessStateMutator.report(id, { loadedAt: new Date(cached.meta.fetchedAt), isDegraded })

      return {
        bytes: new Uint8Array(cached.payload as ArrayBuffer),
        freshness: cached.freshness,
        fromCache: true,
        sourceStamp: cached.meta.sourceStamp
      }
    }

    return null
  }

  const sourceStamp = readSourceStamp(response.headers)

  void cache.write(id, response.response, { sourceStamp, itemCount: null })
  DatasetFreshnessStateMutator.report(id, { loadedAt: new Date(), isDegraded: false })

  return { bytes: new Uint8Array(response.response), freshness: 'fresh', fromCache: false, sourceStamp }
}
